import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { EMPTY_ARRAY_LENGTH, NumberUtils } from '@modeso/aumico-lib-core-fe';
import { ICashFlowOperatingActivities, ICashFlowStatementEntry } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { localizationKeys } from '../../../constants/localizationKeys';
import { CashFlowStatementHelper } from '../../../helpers/cash-flow-statement.helper';
import { ICashFlowOperatingActivitiesComponentInput } from '../../../interfaces/ICashFlowOperatingActivitiesComponentInput.interface';
import { ICashFlowStatementFormEntry } from '../../../interfaces/ICashFlowStatementFormEntry.interface';
const debug = Debug('aumico:aumico-lib-annualreport-fe:CashFlowOperatingActivitiesComponent');
@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-cash-flow-operating-activities-component',
    templateUrl: 'cash-flow-operating-activities.component.html',
    styleUrls: ['cash-flow-operating-activities.component.scss'],
})
export class CashFlowOperatingActivitiesComponent implements OnInit {
    private autoScrollInterval: any = null;
    private lastDragEvent: any = null;



    // Utility to find scrollable parent
    private findScrollableParent(element: HTMLElement): HTMLElement | null {
        let parent = element.parentElement;
        while (parent) {
            if (this.isScrollable(parent)) {
                return parent;
            }
            parent = parent.parentElement;
        }
        return null;
    }

    private isScrollable(element: HTMLElement): boolean {
        const overflowY = window.getComputedStyle(element).overflowY;
        return (overflowY === 'scroll' || overflowY === 'auto') && element.scrollHeight > element.clientHeight;
    }
    // refer to finNumber pipe for reference on values
    numberFormat = '1.2-2';

    private operatingActivitiesData: ICashFlowOperatingActivitiesComponentInput;
    currentSelectedFliToDeleteIndex: number;
    showDeleteModal: boolean;

    @Input() set operatingActivities(value: ICashFlowOperatingActivitiesComponentInput) {
        if (value != null && value.title != null && value.title.trim().length > EMPTY_ARRAY_LENGTH) {
            this.operatingActivitiesTitle = value.title;
        }

        if (value && value.flis != null && value.profitOfTheYearFli) {
            this.operatingActivitiesData = cloneDeep(value);
            this.profitFliName = value.profitOfTheYearFli.name;
            this.setTableHeaders();
        }

        if (this.operatingActivitiesData && this.updateFlisAutomatically && !this.isSavedClick) {
            this.updateFlisValues();
        }
    }

    @Input() isSavedClick = false;

    private savedOperatingActivitiesData: ICashFlowOperatingActivities;
    @Input() set savedOperatingActivities(value: ICashFlowOperatingActivities) {
        if (value && !this.isSavedClick) {
            this.tableEntries = [];
            this.tableHasChanges = false;
            this.editMode = true;

            // using cloneDeep to avoid any future mutations that would cause changes by reference
            this.savedOperatingActivitiesData = cloneDeep(value);
            this.setSavedTableData();
            this.setTitleValue();
            this.updateForm();

            if (this.updateFlisAutomatically) {
                this.updateFlisValues();
            }
        }

        if (this.isSavedClick) {
            this.tableHasChanges = false;
        }
    }

    @Input() updateFlisAutomatically: boolean;

    private isAutoFillApplied: boolean;
    @Input() set autofillApplied(value: boolean) {
        if (value != null && this.operatingActivitiesData != null) {
            this.isAutoFillApplied = value;
            if (!this.editMode) {
                this.setInitialTableData();
                this.initializeForm();
            }
        }
    }
    @Input() reportLocale: string;

    cashFlowTypeStyle = {};

    private formValueChanges: Subscription | undefined;

    form: UntypedFormGroup;

    titleEditMode = false;

    /**
     * Form rows (UI state). This is what the reactive form binds to.
     */
    tableFormEntries: ICashFlowStatementFormEntry[] = [];

    // Keep existing tableEntries as the persisted model (ICashFlowStatementEntry)
    tableEntries: ICashFlowStatementEntry[] = [];

    private intialTableEntriesData: ICashFlowStatementEntry[] = [];

    private local = localizationKeys;
    currentYearDate: string;
    previousYearDate: string;
    currency: string;

    tableHasChanges = false;

    editMode = false;

    dataSource: UntypedFormArray;

    currentTotal: number;
    previousTotal: number;

    operatingActivitiesTitle = '';

    profitFliName: string;

    @Output() totalChangeEvent = new EventEmitter<{ currentTotal: number; previousTotal: number }>();
    constructor(
        private formBuilder: UntypedFormBuilder,

        private localizedTexts: I18NextService,
    ) { }

    ngOnInit(): void {
        debug('ngOnInit');
        this.initializeForm();
        this.applyLocalizations();
        if (this.editMode) {
            this.setSavedTableData();
        } else {
            this.setInitialTableData();
            this.initializeForm();
        }
        this.updateForm();
        this.setBadgeStyles();
    }

    private setBadgeStyles(): void {
        this.cashFlowTypeStyle = {
            2: {
                color: 'light-green',
                text: this.localizedTexts.t('aumico_annual_report_cash_flow_operating_activities', {
                    lng: this.reportLocale,
                }) as string,
            },
            3: {
                color: 'purple',
                text: this.localizedTexts.t('aumico_annual_report_cash_flow_networking_capital', {
                    lng: this.reportLocale,
                }) as string,
            },
        };
    }

    private applyLocalizations(): void {
        this.setTitleValue();
    }

    private setTitleValue(): void {
        if (
            !this.editMode &&
            (this.operatingActivitiesTitle?.trim().length === EMPTY_ARRAY_LENGTH ||
                this.operatingActivitiesTitle == null)
        ) {
            this.operatingActivitiesTitle = this.localizedTexts.t(
                this.local.aumico_annual_report_cash_flow_operating_activities_title,
                {
                    lng: this.reportLocale,
                },
            ) as string;
        } else if (
            this.operatingActivitiesTitle?.trim().length === EMPTY_ARRAY_LENGTH ||
            this.operatingActivitiesTitle == null
        ) {
            this.operatingActivitiesTitle = this.savedOperatingActivitiesData.title;
        }
    }

    private setTableHeaders(): void {
        this.currency = this.operatingActivitiesData.currency;
        this.currentYearDate = this.operatingActivitiesData.currentYearDate;
        this.previousYearDate = this.operatingActivitiesData.previousYearDate;
    }

    private setInitialTableData(): void {
        this.tableEntries = [];
        this.prepareTableEntries();
        this.rebuildFormEntriesFromModel();
    }

    private initializeForm(): void {
        this.form = this.formBuilder.group({
            dataSource: this.formBuilder.array([]),
        });
        this.dataSource = this.getAllAsFormArray();
        this.form.setControl('dataSource', this.dataSource);

        // listen to form change
        this.listenToFormChange();
    }

    private updateForm(): void {
        this.dataSource = this.getAllAsFormArray();
        if (this.form != null) {
            this.form.setControl('dataSource', this.dataSource);
        }
    }

    private rebuildFormEntriesFromModel(): void {
        this.tableFormEntries = (this.tableEntries ?? []).map((e) => {
            const manualCurrent = e.manuallyEnteredCurrentValue ?? null;
            const manualPrevious = e.manuallyEnteredPreviousValue ?? null;
            const systemCurrent = e.calculatedCurrentValue ?? null;
            const systemPrevious = e.calculatedPreviousValue ?? null;

            return {
                name: e.name,
                fliId: e.fliId ?? null,
                isCustom: e.isCustom,
                type: e.type,

                currentValue: manualCurrent !== null ? manualCurrent : systemCurrent,
                previousValue: manualPrevious !== null ? manualPrevious : systemPrevious,

                systemCurrentValue: systemCurrent,
                systemPreviousValue: systemPrevious,

                manuallyEnteredCurrentValue: manualCurrent,
                manuallyEnteredPreviousValue: manualPrevious,
            };
        });
    }

    private getAllAsFormArray(): UntypedFormArray {
        this.tableFormEntries = this.tableFormEntries.reduce((acc, current) => {
            if (!acc.some((item) => item.fliId === current.fliId) || current.fliId == null) {
                acc.push(current);
            }
            return acc;
        }, []);

        const formGroups: AbstractControl[] = this.tableFormEntries.map((entry) => this.addFormGroup(entry));

        return new UntypedFormArray(formGroups);
    }

    private addFormGroup(dataSource: ICashFlowStatementFormEntry): UntypedFormGroup {
        const dataSourceFormGroup = {};
        Object.keys(dataSource).forEach((key) => {
            dataSourceFormGroup[key] = new UntypedFormControl((dataSource as any)[key]);
        });

        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private listenToFormChange(): void {
        if (this.formValueChanges != null) {
            this.formValueChanges.unsubscribe();
        }

        this.formValueChanges = this.form.valueChanges
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value) => {
                if (this.tableHasChanges !== true) {
                    this.tableHasChanges = this.form.dirty;
                }

                // sync UI state
                this.tableFormEntries = cloneDeep<ICashFlowStatementFormEntry[]>(value.dataSource);

                // map back into persisted model for totals
                this.tableEntries = this.mapFormEntriesToModelEntries(this.tableFormEntries, this.tableEntries);
                this.setTotals();
            });
    }

    private mapFormEntriesToModelEntries(
        formEntries: ICashFlowStatementFormEntry[],
        existingModelEntries: ICashFlowStatementEntry[],
    ): ICashFlowStatementEntry[] {
        const indexFallback = new Map<string, ICashFlowStatementEntry>();
        (existingModelEntries ?? []).forEach((e) => indexFallback.set(e.fliId ?? e.name, e));

        return (formEntries ?? []).map((fe) => {
            const key = fe.fliId ?? fe.name;
            const base = cloneDeep(indexFallback.get(key)) ?? ({} as ICashFlowStatementEntry);

            const systemCurrent = fe.systemCurrentValue ?? base.calculatedCurrentValue ?? null;
            const systemPrevious = fe.systemPreviousValue ?? base.calculatedPreviousValue ?? null;

            const manualCurrent = this.isManualOverride(fe.currentValue, systemCurrent) ? fe.currentValue : null;
            const manualPrevious = this.isManualOverride(fe.previousValue, systemPrevious) ? fe.previousValue : null;

            return {
                ...base,
                name: fe.name,
                fliId: fe.fliId as any,
                isCustom: fe.isCustom,
                type: fe.type,

                calculatedCurrentValue: systemCurrent,
                calculatedPreviousValue: systemPrevious,

                manuallyEnteredCurrentValue: manualCurrent,
                manuallyEnteredPreviousValue: manualPrevious,
            };
        });
    }

    private isManualOverride(value: number | null, systemValue: number | null): boolean {
        if (value === null || value === undefined) {
            return false;
        }
        if (systemValue === null || systemValue === undefined) {
            return true;
        }
        return !NumberUtils.areNumbersEqual(value, systemValue);
    }

    saveTitleValue($event: string): void {
        this.operatingActivitiesTitle = $event;
        this.tableHasChanges = true;
    }

    addRow(): void {
        this.tableHasChanges = true;
        // Add a model row
        this.tableEntries.push({
            fliId: null,
            name: null,
            calculatedPreviousValue: null,
            calculatedCurrentValue: null,
            manuallyEnteredCurrentValue: null,
            manuallyEnteredPreviousValue: null,
            isCustom: true,
        });
        this.rebuildFormEntriesFromModel();
        this.updateForm();
    }

    private prepareTableEntries(): void {
        if (this.operatingActivitiesData != null) {
            const operatingActivitiesData: ICashFlowOperatingActivitiesComponentInput =
                cloneDeep<ICashFlowOperatingActivitiesComponentInput>(this.operatingActivitiesData);

            // First entry must be for annual profit
            this.initializeProfitFliValuesTableEntry();

            this.tableEntries.push(
                ...CashFlowStatementHelper.prepareTableEntriesForOperatingActivities(
                    operatingActivitiesData.flis,
                    this.isAutoFillApplied,
                ),
            );
            this.intialTableEntriesData = cloneDeep(this.tableEntries);
            this.setTotals();
        }
    }

    private initializeProfitFliValuesTableEntry(): void {
        let calculatedPreviousValue: number | null = null;
        if (NumberUtils.isFinite(this.operatingActivitiesData.previousAnnualProfit)) {
            calculatedPreviousValue = NumberUtils.roundNumber(this.operatingActivitiesData.previousAnnualProfit);
        }
        let calculatedCurrentValue: number | null = null;
        if (NumberUtils.isFinite(this.operatingActivitiesData.currentAnnualProfit)) {
            calculatedCurrentValue = NumberUtils.roundNumber(this.operatingActivitiesData.currentAnnualProfit);
        }
        this.tableEntries.push({
            name: this.operatingActivitiesData.profitOfTheYearFli.caption,
            calculatedCurrentValue,
            calculatedPreviousValue,
            manuallyEnteredCurrentValue: null,
            manuallyEnteredPreviousValue: null,
            fliId: this.operatingActivitiesData.profitOfTheYearFli.name,
        });
    }

    private setSavedTableData(): void {
        this.tableEntries = cloneDeep(this.savedOperatingActivitiesData.entries);
        this.intialTableEntriesData = cloneDeep(this.tableEntries);
        this.operatingActivitiesTitle = this.savedOperatingActivitiesData.title;
        this.rebuildFormEntriesFromModel();
        this.setTotals();
    }

    private setTotals(): void {
        const totals: { currentTotal: number; previousTotal: number } =
            CashFlowStatementHelper.calculateTableEntriesTotals(this.tableEntries);
        let currentTotal = 0;
        let previousTotal = 0;
        if (NumberUtils.isFinite(totals.currentTotal)) {
            currentTotal = NumberUtils.roundNumber(totals.currentTotal);
        }

        if (NumberUtils.isFinite(totals.previousTotal)) {
            previousTotal = NumberUtils.roundNumber(totals.previousTotal);
        }
        this.currentTotal = currentTotal;
        this.previousTotal = previousTotal;

        this.totalChangeEvent.emit({ currentTotal: this.currentTotal, previousTotal: this.previousTotal });
    }

    getOperatingActivitiesFormValue(): ICashFlowOperatingActivities {
        // Ensure we persist manual overrides into manuallyEntered* and system into calculated*
        const modelEntries = CashFlowStatementHelper.mapStringValuesInOperatingAndFinancingTablesToNumbers(
            this.tableEntries,
        );

        return {
            title: this.operatingActivitiesTitle,
            entries: modelEntries,
        };
    }

    onListDrop(event: CdkDragDrop<ICashFlowStatementEntry[]>): void {
        debug(`Moving item from ${event.previousIndex} to index ${event.currentIndex}`);

        moveItemInArray(this.tableEntries, event.previousIndex, event.currentIndex);

        if (fastDeepEqual(this.intialTableEntriesData, this.tableEntries)) {
            this.tableHasChanges = false;
        } else {
            this.tableHasChanges = true;
        }
        this.rebuildFormEntriesFromModel();
        this.updateForm();
    }

    titleEdited(): void {
        this.titleEditMode = !this.titleEditMode;
    }

    /** trackBy for the rows *ngFor: reuse DOM nodes by position instead of destroying and recreating
     *  every row on each form rebuild / change-detection pass — essential for large tables. */
    trackByIndex(index: number): number {
        return index;
    }

    /** REV-21417: above this many rows, render via a cdk-virtual-scroll viewport so only the visible
     *  rows are instantiated — a plain table with thousands of rows freezes / OOMs the tab. */
    readonly VIRTUAL_ROW_THRESHOLD = 150;
    /** Fixed row height (px) for the virtual-scroll viewport — MUST match .cfs-vrow height in the SCSS. */
    readonly VIRTUAL_ROW_HEIGHT = 54;

    get isVirtualized(): boolean {
        return (this.dataSource?.controls?.length ?? 0) > this.VIRTUAL_ROW_THRESHOLD;
    }

    private updateFlisValues(): void {
        if (this.operatingActivitiesData) {
            // Update only model system values
            this.tableEntries = CashFlowStatementHelper.updateOperatingActivitiesFlisValuesWithShadowCopy(
                this.tableEntries,
                this.operatingActivitiesData,
                this.savedOperatingActivitiesData,
            );
            // Rebuild UI rows (keeps manual overrides displayed)
            this.rebuildFormEntriesFromModel();
        }

        this.updateForm();
        this.setTotals();
    }

    removeAt(index: number): void {
        this.currentSelectedFliToDeleteIndex = index;
        this.showDeleteModal = true;
        debug('remove index', index);
    }

    onCancelDelete(): void {
        this.currentSelectedFliToDeleteIndex = null;
        this.showDeleteModal = false;
    }

    onDeleteFliRow(): void {
        this.tableHasChanges = true;
        this.tableEntries.splice(this.currentSelectedFliToDeleteIndex, 1);
        this.rebuildFormEntriesFromModel();
        this.updateForm();
        this.currentSelectedFliToDeleteIndex = null;
        this.showDeleteModal = false;
    }

    /// Drag and Drop Methods ///
    onRowDragMoved(event: any) {
        this.lastDragEvent = event;
        const edgeThreshold = 60; // px from top/bottom to start scrolling
        const scrollSpeed = 20; // px per interval
        const pointerY = event.pointerPosition.y;
        // Find the scrollable parent of the table
        const tableElement = (event.source && event.source.element) ? event.source.element.nativeElement : null;
        let scrollContainer = null;
        if (tableElement) {
            scrollContainer = this.findScrollableParent(tableElement);
        }
        if (!scrollContainer) {
            scrollContainer = document.getElementById('mainscrollcontent');
        }
        if (!scrollContainer) return;
        const rect = scrollContainer.getBoundingClientRect();

        // Scroll up
        if (pointerY < rect.top + edgeThreshold) {
            this.startAutoScroll(-scrollSpeed, scrollContainer, event);
        }
        // Scroll down
        else if (pointerY > rect.bottom - edgeThreshold) {
            this.startAutoScroll(scrollSpeed, scrollContainer, event);
        } else {
            this.stopAutoScroll();
        }
    }

    startAutoScroll(delta: number, scrollContainer: HTMLElement, dragEvent?: any) {
        if (this.autoScrollInterval) return;
        this.autoScrollInterval = setInterval(() => {
            scrollContainer.scrollTop += delta;
            // Simulate mousemove event at current pointer position
            if (dragEvent && dragEvent.pointerPosition) {
                const evt = new MouseEvent('mousemove', {
                    clientX: dragEvent.pointerPosition.x,
                    clientY: dragEvent.pointerPosition.y,
                    bubbles: true
                });
                document.dispatchEvent(evt);
            }
        }, 16); // ~60fps
    }

    stopAutoScroll() {
        if (this.autoScrollInterval) {
            clearInterval(this.autoScrollInterval);
            this.autoScrollInterval = null;
        }
    }

    onRowDragEnded() {
        this.stopAutoScroll();
        this.lastDragEvent = null;
    }

}